import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, getGAMeasurementId, isAnalyticsEnabled } from '@/utils/analytics';

/**
 * Analytics component that handles:
 * - Loading Google Analytics 4 script
 * - Tracking page views on route changes
 *
 * Include this component once at the top level of your app (in App.tsx or main.tsx)
 */
export function Analytics() {
  const location = useLocation();

  // Initialize Google Analytics script on mount
  useEffect(() => {
    const measurementId = getGAMeasurementId();

    if (!measurementId) {
      if (import.meta.env.DEV) {
        console.log('[Analytics] No GA Measurement ID configured. Set VITE_GA_MEASUREMENT_ID in .env');
      }
      return;
    }

    // Check if script is already loaded
    if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
      return;
    }

    // Skip loading in development unless explicitly enabled
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalhost && import.meta.env.VITE_ANALYTICS_DEBUG !== 'true') {
      if (import.meta.env.DEV) {
        console.log('[Analytics] Disabled on localhost. Set VITE_ANALYTICS_DEBUG=true to enable.');
      }
      return;
    }

    // Create and inject the gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false, // We'll handle page views manually for SPA
    });

    if (import.meta.env.DEV) {
      console.log('[Analytics] Google Analytics initialized with ID:', measurementId);
    }
  }, []);

  // Track page views on route changes
  useEffect(() => {
    // Use a small delay to ensure the page title is updated
    const timer = setTimeout(() => {
      trackPageView(location.pathname + location.search, document.title);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // This component doesn't render anything
  return null;
}

/**
 * Hook to access analytics tracking functions
 * Provides a convenient way to track events from any component
 */
export function useAnalytics() {
  return {
    isEnabled: isAnalyticsEnabled,
    trackPageView,
  };
}
